import React from "react";
import { gql } from "@apollo/client";
import { useForm } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { SwitchField } from "@syos-music/form";
import {
  InstrumentTypeSelectField,
  MaterialColorSelectField,
  MouthpieceTipOpening,
  MouthpieceTipOpeningSelectField,
} from "@syos-music/instruments";

import { Checkbox, FormControlLabel, Grid, Stack } from "@mui/material";
import SignatureMouthpieceSelectField from "components/signatureMouthpiece/SignatureMouthpieceSelectField";

const SIGNATURE_MOUTHPIECE_FRAGMENT = gql`
  fragment OptionsField_SignatureMouthpiece on SignatureMouthpiece {
    id
    signature
    mouthpieceColor
    ligatureColor

    mouthpieceRef {
      id
      instrumentType

      saxophoneMouthpiece {
        id
        tipOpening
      }

      clarinetMouthpiece {
        id
        tipOpening
      }
    }
  }
`;

export default function OptionsField({ isOrderPaid, cartItem, name }) {
  const [autoUpdate, setAutoUpdate] = React.useState(false);
  const signatureMouthpieceSelectRef = React.useRef();

  const { instrument } = cartItem.product.defaultOptions;

  const form = useForm();
  const instrumentType = form.getFieldState(`${name}.instrumentType`)?.value;
  const addLigature = form.getFieldState(`${name}.addLigature`)?.value;

  return (
    <Grid container>
      <Grid item xs={12}>
        <Stack spacing={2} direction="row" alignItems="center">
          <SignatureMouthpieceSelectField
            filters={{ instrument: [instrument] }}
            name={`${name}.signatureMouthpieceId`}
            ref={signatureMouthpieceSelectRef}
            fragment={SIGNATURE_MOUTHPIECE_FRAGMENT}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={autoUpdate}
                onChange={(evt, checked) => setAutoUpdate(checked)}
              />
            }
            label="Mise à jour auto"
            sx={{ mt: 2 }}
          />

          <OnChange name={`${name}.signatureMouthpieceId`}>
            {() => {
              const {
                signature,
                mouthpieceColor,
                ligatureColor,
                mouthpieceRef: {
                  instrument,
                  instrumentType,
                  saxophoneMouthpiece,
                  clarinetMouthpiece,
                },
              } = signatureMouthpieceSelectRef.current.signatureMouthpiece;

              form.change(`${name}.signature`, signature);

              if (!autoUpdate) return;

              form.change(`${name}.instrumentType`, instrumentType);
              form.change(`${name}.mouthpieceColor`, mouthpieceColor);
              form.change(`${name}.ligatureColor`, ligatureColor);

              const tipOpeningValue =
                clarinetMouthpiece?.tipOpening ??
                saxophoneMouthpiece?.tipOpening;

              const tipOpening = MouthpieceTipOpening.fromMilimeters(
                instrument,
                instrumentType,
                tipOpeningValue / 100
              )?.key;

              form.change(`${name}.tipOpening`, tipOpening);
            }}
          </OnChange>
        </Stack>
      </Grid>

      <Grid item xs={12} sm={6}>
        <InstrumentTypeSelectField
          instrument={instrument}
          name={`${name}.instrumentType`}
        />
      </Grid>

      {instrument === "saxophone" && (
        <Grid item xs={12} sm={6}>
          <MouthpieceTipOpeningSelectField
            name={`${name}.tipOpening`}
            instrument={instrument}
            instrumentType={instrumentType}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <MaterialColorSelectField
          name={`${name}.mouthpieceColor`}
          label="Couleur bec"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <SwitchField
          name={`${name}.addLigature`}
          label="Ajouter une ligature"
          sx={{ mt: 2 }}
          disabled
          helperText="Cette options n'est plus disponible, vous devez ajouter les ligatures & couvre-becs manuellement."
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        {addLigature && (
          <MaterialColorSelectField
            name={`${name}.ligatureColor`}
            label="Couleur ligature"
          />
        )}
      </Grid>
    </Grid>
  );
}
